import { createContext, useContext, useEffect, useRef, useState, ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { trackFunnelEvent, markVisitorAsAdmin, clearAdminFlag } from '@/hooks/useFunnelTracking';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  isAdmin: boolean;
  isSuperAdmin: boolean;
  mfaRequired: boolean | null;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signUp: (email: string, password: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface CachedAdminAccessState {
  userId: string;
  isAdmin: boolean;
  isSuperAdmin: boolean;
  mfaRequired: boolean;
}

let cachedAdminAccessState: CachedAdminAccessState | null = null;
let inFlightAdminAccessUserId: string | null = null;
let inFlightAdminAccessPromise: Promise<void> | null = null;

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [roleLoading, setRoleLoading] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [mfaRequired, setMfaRequired] = useState<boolean | null>(null);
  const isMountedRef = useRef(false);
  const activeUserIdRef = useRef<string | null>(null);

  const applyAdminAccessState = (accessState: CachedAdminAccessState) => {
    setIsAdmin(accessState.isAdmin);
    setIsSuperAdmin(accessState.isSuperAdmin);
    setMfaRequired(accessState.isAdmin ? accessState.mfaRequired : false);

    if (accessState.isAdmin) {
      markVisitorAsAdmin();
      // Stop any analytics recording immediately for admin sessions —
      // staff/internal usage must never appear in PostHog/OpenReplay.
      import('@/lib/posthogInit').then(m => m.stopPostHog()).catch(() => {});
      import('@/lib/openreplayInit').then(m => m.stopOpenReplay()).catch(() => {});
    } else {
      clearAdminFlag();
    }
  };

  const resetAdminAccessState = () => {
    setIsAdmin(false);
    setIsSuperAdmin(false);
    setMfaRequired(null);
    clearAdminFlag();
  };

  const loadAdminAccessState = async (userId: string) => {
    if (cachedAdminAccessState?.userId === userId) {
      if (isMountedRef.current && activeUserIdRef.current === userId) {
        applyAdminAccessState(cachedAdminAccessState);
        setRoleLoading(false);
      }
      return;
    }

    if (inFlightAdminAccessUserId === userId && inFlightAdminAccessPromise) {
      setRoleLoading(true);
      return inFlightAdminAccessPromise;
    }

    setRoleLoading(true);

    const request = (async () => {
      try {
        const { data, error } = await supabase
          .from('user_roles')
          .select('role, mfa_required')
          .eq('user_id', userId)
          .in('role', ['admin', 'super_admin']);

        if (error) throw error;

        const roles = data ?? [];
        const nextAccessState: CachedAdminAccessState = {
          userId,
          isAdmin: roles.length > 0,
          isSuperAdmin: roles.some(({ role }) => role === 'super_admin'),
          mfaRequired: roles.length > 0 ? roles.some(({ mfa_required }) => mfa_required ?? true) : false,
        };

        cachedAdminAccessState = nextAccessState;

        if (isMountedRef.current && activeUserIdRef.current === userId) {
          applyAdminAccessState(nextAccessState);
        }
      } catch (err) {
        console.error('Error checking admin role:', err);

        if (isMountedRef.current && activeUserIdRef.current === userId) {
          resetAdminAccessState();
        }
      } finally {
        if (inFlightAdminAccessUserId === userId) {
          inFlightAdminAccessUserId = null;
          inFlightAdminAccessPromise = null;
        }

        if (isMountedRef.current && activeUserIdRef.current === userId) {
          setRoleLoading(false);
        }
      }
    })();

    inFlightAdminAccessUserId = userId;
    inFlightAdminAccessPromise = request;

    return request;
  };

  useEffect(() => {
    isMountedRef.current = true;

    const handleSession = (nextSession: Session | null) => {
      if (!isMountedRef.current) return;

      const nextUser = nextSession?.user ?? null;
      activeUserIdRef.current = nextUser?.id ?? null;

      setSession(nextSession);
      setUser(nextUser);
      setAuthLoading(false);

      if (nextUser) {
        if (cachedAdminAccessState?.userId === nextUser.id) {
          applyAdminAccessState(cachedAdminAccessState);
          setRoleLoading(false);
          return;
        }

        if (inFlightAdminAccessUserId === nextUser.id && inFlightAdminAccessPromise) {
          setRoleLoading(true);
          return;
        }

        void loadAdminAccessState(nextUser.id);
      } else {
        cachedAdminAccessState = null;
        inFlightAdminAccessUserId = null;
        inFlightAdminAccessPromise = null;
        resetAdminAccessState();
        setRoleLoading(false);
      }
    };

    // Listen for auth changes FIRST
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, nextSession) => {
      handleSession(nextSession);
    });

    // THEN get initial session
    supabase.auth.getSession().then(({ data: { session: nextSession } }) => {
      handleSession(nextSession);
    });

    return () => {
      isMountedRef.current = false;
      subscription.unsubscribe();
    };
  }, []);

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (!error) {
      trackFunnelEvent('user_logged_in', { method: 'email' });
    }
    return { error: error as Error | null };
  };

  const signUp = async (email: string, password: string) => {
    const redirectUrl = `${window.location.origin}/`;
    const { error } = await supabase.auth.signUp({ 
      email, 
      password,
      options: {
        emailRedirectTo: redirectUrl
      }
    });
    if (!error) {
      trackFunnelEvent('user_signed_up', { method: 'email' });
    }
    return { error: error as Error | null };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    cachedAdminAccessState = null;
    inFlightAdminAccessUserId = null;
    inFlightAdminAccessPromise = null;
    resetAdminAccessState();
  };

  const loading = authLoading || roleLoading;

  return (
    <AuthContext.Provider value={{ user, session, loading, isAdmin, isSuperAdmin, mfaRequired, signIn, signUp, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
